// J-244 (the owner's order of 2026-10-16, item 3): the page an account is sent to while it must change
// its password: a new account an admin created, or one whose password an admin reset. Until the change
// succeeds the server refuses every other API call (403 `password_change_required`), so this page sits
// outside the shell, whose reads would all be refused, and needs only the session read. An account
// without the flag is sent to Settings' own page.
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@vbltc/ui/button";
import { StatusRegion } from "@vbltc/ui/components/status-region/status-region";
import { AUTH_LINK_CLASS } from "@vbltc/ui";
import { AuthPage } from "../app/auth-page";
import { authClient } from "../features/auth/client";
import { ChangePasswordForm } from "../features/auth/components/change-password-form";
import { mustChangePassword } from "../features/auth/password-change";
import { readSession, SessionUnavailableError } from "../features/auth/session-read";
import { setTelemetryUser } from "../telemetry";

export const Route = createFileRoute("/change-password")({
  beforeLoad: async () => {
    const read = await readSession(() => authClient.getSession());
    if (read.kind === "unavailable") throw new SessionUnavailableError(read.status);
    if (read.kind === "signed-out")
      throw redirect({ to: "/login", search: { redirect: undefined, reason: undefined } });
    if (!mustChangePassword(read.session)) throw redirect({ to: "/settings/password" });
    setTelemetryUser(read.session.user.id); // opaque id only (T7/T12)
    return { session: read.session };
  },
  component: ChangePasswordPage,
});

function ChangePasswordPage() {
  const { session } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  async function signOut() {
    await authClient.signOut();
    queryClient.clear(); // no PHI stays in memory (T12)
    setTelemetryUser(undefined);
    await navigate({ to: "/login", search: { redirect: undefined, reason: undefined } });
  }

  return (
    // J-242: the sign-in page's frame, so the forced change looks like the step after signing in.
    <AuthPage
      heading="Change your password"
      description="Your password was set by an administrator. Choose your own password to continue."
    >
      <StatusRegion
        tone="moderate"
        message={`Signed in as ${session.user.email}.`}
        className="mb-4.5 rounded-md px-4"
      />
      <ChangePasswordForm appearance="auth" onChanged={() => void navigate({ to: "/" })} />
      <Button
        type="button"
        variant="link"
        className={`${AUTH_LINK_CLASS} mt-2 flex`}
        data-telemetry="password-change-sign-out"
        onClick={() => void signOut()}
      >
        Sign out
      </Button>
    </AuthPage>
  );
}
